import {
  editorForPath,
  ConnectionManager,
  DockPointerData,
  dataManager,
  Layout,
  PageId,
  TypeId,
  ViewType,
  type IDockPointer,
  type UiCommandMessage,
} from '@sdk';
import { useEffect } from 'react';
import { NavigationActions } from '@src/navigation/NavigationActions';
import { DockPointer } from '@src/navigation/DockPointer';
import { AssetDocPointer } from '@src/navigation/AssetDocPointer';
import { isAppType } from '@src/navigation/app-dock';
import {
  dockPointerForClickTarget,
  renderDesktopNotification,
  type NotificationClickTarget,
  type NotificationPayload,
} from '@src/notifications/renderDesktopNotification';
import { openInstallRequest } from '@src/components/install/install-request-store';
import { closeAskModal, openAskModal, useAskModalStore } from '@src/components/ask/ask-modal-store';
import { openWizardPopup } from '@src/components/assets/editor/wizard/wizard-popup-store';
import { deliverClaimedQuestion } from '@src/components/ask/ask-claims';

/** The subset of the Electron preload bridge this hook uses. */
interface NotifyBridge {
  /**
   * Subscribe to main-process banner clicks. Returns a disposer — but only on a
   * shell new enough to hand one back: the desktop shell and this UI ship as
   * SEPARATELY VERSIONED artifacts (an installed shell can be months behind the
   * UI it loads), so the return is optional and every caller must degrade.
   */
  onNotificationClick?: (cb: (data: { clickTarget?: NotificationClickTarget }) => void) => (() => void) | void;
}

/**
 * Listen for server-side `ui_command` WS messages and execute them.
 *
 * Mounted once at the app root (outside the react-router context). We avoid
 * `useNavigate()` here by building the target URL with the same helper
 * react-router uses internally (`buildDockUrl`), then pushing history state
 * and firing `popstate` — which the `createBrowserRouter` listens for.
 *
 * Today we handle `kind === 'navigate_entity'`: fetch the entity (cache,
 * then network if needed), read its `dockPointer`, and rewrite the URL.
 * Unknown `kind` values are ignored — forward-compat so adding new command
 * types on the server doesn't require a matched UI deploy.
 */
export function useUiCommandListener(): void {
  useEffect(() => {
    const cm = ConnectionManager.getInstance();

    // Rewrite the URL to a dock pointer. This listener is mounted above the
    // router, so it commits through the detached entry point rather than
    // keeping its own copy of the push-and-popstate idiom.
    const navigateTo = (pointer: IDockPointer) => NavigationActions.commitDetached(pointer);

    const handleNavigateEntity = async (msg: UiCommandMessage) => {
      if (!msg.type || !msg.id) {
        console.warn('[ui_command] navigate_entity missing type/id', msg);
        return;
      }

      let typeId: TypeId;
      try {
        typeId = new TypeId(msg.type, msg.id);
      } catch (err) {
        console.warn('[ui_command] invalid TypeId', msg, err);
        return;
      }

      // An app (web app, artifact, endpoint) opens running — the same dock `flow show` opens it in.
      if (isAppType(typeId.type)) {
        navigateTo(new DockPointerData(ViewType.APP, typeId.toString()));
        return;
      }

      // Cache-first; fall back to network. The server already confirmed
      // existence, so the entity is real — we only try to resolve it to
      // get entity-specific `dockPointer` overrides (Shell → SHELL view,
      // AgenticProcess → SHELL view, etc.). If the type has no TS class
      // registered, or the fetch fails, we fall back to the generic
      // HOME + typeId pointer — navigation still works, we just land on
      // the entity's home view instead of its bespoke one.
      let entity = dataManager.getByTypeIdFromCache(typeId);
      if (!entity) {
        try {
          entity = await dataManager.getByTypeId(typeId);
        } catch {
          entity = null;
        }
      }

      const pointer: IDockPointer =
        entity && entity.dockPointer && entity.dockPointer.viewType
          ? entity.dockPointer
          : new DockPointerData(ViewType.HOME, typeId.toString());
      navigateTo(pointer);
    };

    // `navigate_vfs`: open a raw file path in the asset editor — no entity
    // required (the server fell back to this because the path isn't indexed).
    // Editor is chosen by extension; the path is the unique vfs address.
    const handleNavigateVfs = (msg: UiCommandMessage) => {
      if (!msg.path) {
        console.warn('[ui_command] navigate_vfs missing path', msg);
        return;
      }
      const editor = editorForPath(msg.path);
      navigateTo(AssetDocPointer.forVfs(editor, msg.path).toDockPointer());
    };

    // `navigate_dock`: go to a SCREEN. Unlike the two above there is nothing to
    // resolve — the backend validated the view and its pointer requirement
    // against the dock-address table, and sends the frontend's own field names,
    // so this is a construction.
    const handleNavigateDock = (msg: UiCommandMessage) => {
      if (!msg.view_type) {
        console.warn('[ui_command] navigate_dock missing view_type', msg);
        return;
      }
      // A question from a wizard run a screen is showing is drawn THERE, not by sending the tab away.
      if ((msg.view_type as ViewType) === ViewType.ASK && msg.pointer && deliverClaimedQuestion(msg.run, msg.pointer))
        return;
      navigateTo(
        new DockPointer(
          msg.view_type as ViewType,
          msg.pointer ?? undefined,
          msg.options ?? undefined,
          // The layout the backend asked for. This argument used to be
          // hardcoded `undefined`, so a pushed command could only ever land in
          // the dock — there was no way to raise a chrome-less `win/` window
          // from the backend at all.
          (msg.layout as Layout | undefined) ?? undefined,
          msg.page as PageId | undefined,
        ),
      );
    };

    const onUiCommand = (msg: UiCommandMessage) => {
      if (msg.kind === 'navigate_entity') {
        void handleNavigateEntity(msg);
        return;
      }
      if (msg.kind === 'navigate_dock') {
        handleNavigateDock(msg);
        return;
      }
      if (msg.kind === 'navigate_vfs') {
        handleNavigateVfs(msg);
        return;
      }
      if (msg.kind === 'desktop_notify') {
        renderDesktopNotification((msg.info ?? {}) as NotificationPayload);
        return;
      }
      // `open_link`: the hub asked this desktop to open a received message (its email landing page).
      // The backend already fetched and synced it; this is the same `/dock/home?action=open…` the deep
      // link lands on, so `IncomingDeepLink` takes it from here. The desktop window comes forward first.
      if (msg.kind === 'open_link') {
        (window as unknown as { electronAPI?: { focusWindow?: () => void } }).electronAPI?.focusWindow?.();
        window.location.assign(`/dock/home?${new URLSearchParams(msg.query ?? {}).toString()}`);
        return;
      }
      // `install_request`: the hub relayed a one-click install to this desktop.
      // Open the Add-asset dialog with the published row; the person picks the
      // project and confirms there — nothing is written until then.
      if (msg.kind === 'install_request') {
        if (msg.request?.typeid) openInstallRequest(msg.request);
        else console.warn('[ui_command] install_request without a typeid', msg);
        return;
      }
      // `open_ask_modal`: a ComputeOp `ask` was raised and this tab is live.
      // Open it as a dialog over whatever the tab is already showing — never a
      // navigation, so a wizard's own progress page (or anything else) stays
      // visible right behind it. See `ask_window.py::_push_to_live_tab`.
      if (msg.kind === 'open_ask_modal') {
        // A setup screen showing the run that asked draws it in place instead (`ask-claims`).
        if (msg.pointer && deliverClaimedQuestion(msg.run, msg.pointer)) return;
        if (msg.pointer) openAskModal(msg.pointer);
        else console.warn('[ui_command] open_ask_modal without a pointer', msg);
        return;
      }
      // `open_wizard_popup`: a popup wizard (first-run setup) is being put in front of this live tab.
      // A dialog over whatever the tab is showing — never a navigation, so the page stays as it was.
      if (msg.kind === 'open_wizard_popup') {
        if (msg.pointer) openWizardPopup(msg.pointer);
        else console.warn('[ui_command] open_wizard_popup without a pointer', msg);
        return;
      }
      // `close_ask_modal`: the op stopped waiting (its run was replaced). Close
      // only if this tab still shows THAT question — a newer one stays open.
      if (msg.kind === 'close_ask_modal') {
        if (msg.pointer && useAskModalStore.getState().payload === msg.pointer) closeAskModal();
        return;
      }
      // Forward-compat: log unknown kinds but don't crash.
      console.debug('[ui_command] unhandled kind', msg.kind);
    };

    cm.on('on_ui_command', onUiCommand);

    // Banner click (from main process) → navigate to the payload's generic
    // click target (URL-first, works for any notify_type — the OS badge is
    // handled separately by useSyncOsBadge, driven by StreamInboxManager.unread).
    const bridge = (window as unknown as { electronAPI?: NotifyBridge }).electronAPI;
    const disposeNotificationClick = bridge?.onNotificationClick?.(({ clickTarget }) => {
      const pointer = dockPointerForClickTarget(clickTarget);
      if (pointer) navigateTo(pointer);
    });

    return () => {
      cm.off('on_ui_command', onUiCommand);
      // Release the bridge subscription too — without this every remount added
      // another IPC listener (MaxListenersExceededWarning at 11), and each one
      // navigates, so a single banner click fired `navigateTo` once per leak.
      // Guarded rather than called blindly: an older shell's preload returns
      // nothing, and we must stay mountable on it.
      if (typeof disposeNotificationClick === 'function') disposeNotificationClick();
    };
  }, []);
}
